/* oxlint-disable max-lines */
import {
  GeoSuggest as StructuredGeoSuggest,
  IMultiSuggestResult,
  TSuggestType,
  ESuggestType,
  IMessageInfo,
  EInputType,
  IItemClickParams,
  ETruncation,
} from '@cian/geosuggest-widget';
import { Spinner } from '@cian/ui-kit/loader';
import { IconActionSearch16 } from '@cian/ui-kit-design-tokens/icons';
import { isEmpty, last } from 'ramda';
import * as React from 'react';

import styles from './styles.css';
import {
  trackGeoSuggestClick,
  trackGeoSuggestNotFound,
  trackGeoSuggestStartTyping,
  trackNewbuildingSuggestLinkClick,
  trackSelectGeo,
} from '../../../../../shared/utils/analytics/trackGeoSuggestEvents';
import { useDebouncedCallback } from 'shared/hooks';
import { FDealType, FOfferType } from '../../../../../packages/JsonQuery';
import { IDirection } from 'shared/types/geo';
import { ILocation, TLocation } from 'shared/types/location';
import {
  IDistrict,
  IGeocodeCachedResult,
  IGeoDetail,
  IGeoObject,
  IPolygonObject,
  ISelectBSCenterParams,
  ISelectCoworkingParams,
  ISelectKPParams,
  TGeoType,
} from 'shared/types/geoSuggest';
import {
  createLocationModel,
  geocode,
  getDistrict,
  getGeoSuggestLocation,
  getMultiSuggest,
  getRegion,
  getSuggestOfferType,
  isNewbuildingsAvailable,
  removeCountryName,
} from './utils';
import { useApplicationContext } from 'shared/utils/applicationContext';
import { geocodeForSearch } from 'shared/services/geocodeForSearch';
import { getRegionId } from 'shared/utils/geo';
import { EQueryParameters, EQueryParameters_2 } from 'shared/repositories/geo-suggest/v2/suggest';
import { clsx } from 'clsx';
import { updateLocalStorageFiltersWereApplied } from 'shared/utils/uxfbLocalStorage/uxfbLocalStorage';

export type IGeocodeResult = IGeocodeCachedResult;

const DEAL_TYPE_SUGGEST_MAP: Partial<Record<FDealType, EQueryParameters>> = {
  [FDealType.Sale]: EQueryParameters.Sale,
  [FDealType.RentLongterm]: EQueryParameters.Rent,
  [FDealType.RentDaily]: EQueryParameters.Rent,
  [FDealType.Rent]: EQueryParameters.Rent,
};

const GEO_TYPE_MAP: Partial<Record<ESuggestType, TGeoType>> = {
  [ESuggestType.city]: 'location',
  [ESuggestType.district]: 'district',
  [ESuggestType.underground]: 'underground',
  [ESuggestType.newbuilding]: 'newobject',
  [ESuggestType.builder]: 'builder',
  [ESuggestType.road]: 'highway',
  [ESuggestType.railway]: 'railway',
  [ESuggestType.village]: 'village',
  [ESuggestType.regionalDistrict]: 'regional_district_id',
};

export interface IGeoSuggestProps {
  highwaysData: IDirection[];
  dealType: FDealType;
  offerType: FOfferType;
  currentLocation: TLocation;
  boundedBy: [YMaps.TCoord, YMaps.TCoord] | undefined;
  rightAddon?: React.ReactNode;
  isRedesign?: boolean;

  onGeoSelected(value: IGeoObject | IPolygonObject, userInput: string): void;
  onHighwaySelected(highwaysIds: number[], regionId?: number): void;
  onKPSelected(params: ISelectKPParams): void;
  onBuilderSelected({ builderId, name }: { builderId: number; name: string }): void;
  onBSCenterSelected?(params: ISelectBSCenterParams): void;
  onCoworkingSelected?(params: ISelectCoworkingParams): void;
}

export interface IGeoSuggestState {
  value: string;
  selectedValue: string;
  loading: boolean;
  disabled: boolean;
  error: IMessageInfo;
  suggestData?: IMultiSuggestResult;
}

type TGeoModel = Partial<IGeoObject | IPolygonObject> & { isParsed?: boolean };

const ERROR = { title: 'Не удалось получить возможные варианты', text: 'Попробуйте еще раз' };
const NOT_FOUND = { title: 'Ничего не найдено', text: 'Укажите другой адрес' };
const YANDEX_RESULTS_MAX = 10;

/** Типы, для которых в подсказке по адресу ищем ещё и по названию бизнес-центра */
const BUSINESS_CENTER_OFFER_TYPES = FOfferType.Office | FOfferType.Coworking | FOfferType.Workplace;
/** Типы, для которых ищем по названию торгового центра */
const SHOPPING_CENTER_OFFER_TYPES = FOfferType.TradeArea;

export const GeoSuggest: React.FC<IGeoSuggestProps> = ({
  dealType,
  offerType,
  currentLocation,
  rightAddon,
  onHighwaySelected,
  onGeoSelected,
  onBuilderSelected,
  onKPSelected,
  onBSCenterSelected,
  onCoworkingSelected,
  boundedBy,
  isRedesign = false,
}) => {
  const context = useApplicationContext();
  const { logger } = context;
  const [value, setValue] = React.useState('');
  const [selectedValue, setSelectedValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [error, setError] = React.useState({});
  const [suggestData, setSuggestData] = React.useState<IMultiSuggestResult>();
  const [inputStarted, setInputStarted] = React.useState(false);

  const placeholder = React.useMemo(() => {
    const commercialOfferType = offerType & FOfferType.Commercial;

    if (commercialOfferType !== 0) {
      // Офис / офис в коворкинге / рабочее место — ищем по названию БЦ
      if ((commercialOfferType & ~BUSINESS_CENTER_OFFER_TYPES) === 0) {
        return 'Название БЦ, адрес';
      }

      // Торговая площадь — по названию ТЦ
      if ((commercialOfferType & ~SHOPPING_CENTER_OFFER_TYPES) === 0) {
        return 'Название ТЦ, адрес';
      }

      // Остальная коммерция (склад, ПСН, здание, производство, земля, бизнес) — только адрес
      return 'Адрес';
    }

    if (isNewbuildingsAvailable(dealType, offerType, currentLocation)) {
      return 'Адрес, ЖК или Застройщик';
    }

    return 'Адрес';
  }, [currentLocation, dealType, offerType]);

  const suggest = useDebouncedCallback(async (valueRaw: string) => {
    const query = valueRaw.trim();

    if (query) {
      setIsLoading(true);

      if (!inputStarted) {
        setInputStarted(true);
        trackGeoSuggestStartTyping();
      }

      try {
        const result = await getMultiSuggest(context, {
          structured: {
            offerType: getSuggestOfferType(offerType),
            query,
            regionId: getGeoSuggestLocation(currentLocation),
            dealType: DEAL_TYPE_SUGGEST_MAP[dealType],
            source: EQueryParameters_2.Serp,
          },
          yandex: {
            options: { boundedBy, results: YANDEX_RESULTS_MAX },
            value: 'Россия, ' + value,
          },
        });

        let error: IMessageInfo = {};

        if (value) {
          if (isEmpty(result.suggestions)) {
            trackGeoSuggestNotFound(query);
            error = NOT_FOUND;
          }
          setSuggestData(result as IMultiSuggestResult);
          setIsLoading(false);
          setError(error);
        } else {
          setSuggestData(undefined);
          setIsLoading(false);
        }
      } catch (e) {
        setError(ERROR);
        setIsLoading(false);
      }
    } else {
      setInputStarted(false);
    }
  }, 300);

  const resetSuggest = React.useCallback(() => {
    setValue('');
    setSelectedValue('');
    setError({});
    setIsLoading(false);
    setSuggestData(undefined);
    setInputStarted(false);
  }, []);

  const handleFocus = React.useCallback(() => {
    setError({});

    trackGeoSuggestClick('form');

    if (value.length > 2) {
      suggest(value);
    }
  }, [suggest, value]);

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      setValue(newValue);

      if (newValue.length > 2) {
        suggest(newValue);
      } else {
        setIsLoading(false);
        setError({});
        setSuggestData(undefined);
      }
    },
    [suggest],
  );

  const filterResults = React.useCallback(
    (results: IGeocodeResult[], value: string) => {
      if (results.length === 1) {
        return results[0];
      }

      const filteredResults = results.filter(result => {
        return result.text === value;
      });

      if (filteredResults.length !== 1) {
        logger.warning(`Failed to filter geocode results for: ${value}`);

        return results[0];
      }

      return filteredResults[0];
    },
    [logger],
  );

  const handleGeocodeResults = React.useCallback(
    async (results: IGeocodeResult[] | void, value: string, userInput: string) => {
      if (!results || results.length === 0) {
        logger.warning(`Empty geocode results for: ${value}`);

        setError(ERROR);
        setIsLoading(false);
        setIsDisabled(false);

        return;
      }

      const geocodeResult = filterResults(results, value);
      const [lng, lat] = geocodeResult.coordinates;
      const geocodeForSearchRequest = {
        address: geocodeResult.text,
        kind: geocodeResult.kind,
        lat,
        lng,
      };

      try {
        const result = await geocodeForSearch(context, geocodeForSearchRequest);
        const resultDetails = result.details || [];
        const objectDetails = last(resultDetails);
        const regionInfo: IGeoDetail | undefined = getRegion(result);
        const district: IDistrict | undefined = getDistrict(result);

        const locationInfo: ILocation | undefined = regionInfo ? createLocationModel(regionInfo) : undefined;
        let geoModel: TGeoModel = {
          text: removeCountryName(resultDetails.map(item => item.name).join(', ')).trim(),
          coordinates: [result.geo.lng, result.geo.lat] as [number, number],
          locationId: regionInfo && regionInfo.id,
        };

        if (result.geoLocationCatalogLink) {
          geoModel = {
            ...geoModel,
            type: result.geoLocationCatalogLink.objectType.toLowerCase() as TGeoType,
            id: result.geoLocationCatalogLink.id,
          };
        } else {
          geoModel = {
            ...geoModel,
            // TODO: FUCK IT
            type: objectDetails?.geoType.toLowerCase().replace('road', 'highway') as TGeoType,
            id: objectDetails?.id,
            regionId: result.regionId,
            locationInfo,
            district,
            isParsed: result.isParsed,
          };
        }

        let type = geoModel.type as string;
        // TODO research it. Wait for CD-11447
        if (geoModel.type === 'location') {
          if (resultDetails.length > 1) {
            type = 'city';
          } else {
            type = 'region';
          }
        }

        trackSelectGeo({ geoType: type, isYandexGeo: true });

        if (!result.isParsed && !result.geoLocationCatalogLink) {
          const bounds = geocodeResult.boundedBy;
          (geoModel as IPolygonObject).polygon = [
            [bounds[0][0], bounds[0][1]],
            [bounds[0][0], bounds[1][1]],
            [bounds[1][0], bounds[1][1]],
            [bounds[1][0], bounds[0][1]],
            [bounds[0][0], bounds[0][1]],
          ];
        }

        onGeoSelected(geoModel as IGeoObject, userInput);
        resetSuggest();
        setIsLoading(false);
        setIsDisabled(false);
      } catch (error) {
        logger.warning(`Failed to geocode using 'geocodeForSearch' for: ${JSON.stringify(geocodeForSearchRequest)}`);

        setError(ERROR);
        setIsLoading(false);
        setIsDisabled(false);
      }
    },
    [context, filterResults, logger, onGeoSelected, resetSuggest],
  );

  const handleItemClick = React.useCallback(
    ({ id, title, group, regionId }: IItemClickParams) => {
      if (id) {
        switch (group) {
          case ESuggestType.road:
            onHighwaySelected([id], regionId);
            break;
          case ESuggestType.village:
            onKPSelected({ id, name: title, regionId });
            break;
          case ESuggestType.builder:
            onBuilderSelected({ builderId: id, name: title });
            break;
          case ESuggestType.businessCenter:
          case ESuggestType.shoppingCenter:
            onBSCenterSelected?.({ id, name: title });
            break;
          case ESuggestType.coworking:
            onCoworkingSelected?.({ id, name: title });
            break;
          default: {
            const geo: IGeoObject = {
              id,
              text: title,
              type: GEO_TYPE_MAP[group] || 'location',
              regionId: regionId || getRegionId(currentLocation),
            };

            onGeoSelected(geo, title);
          }
        }

        trackSelectGeo({ geoType: group, isYandexGeo: false });

        resetSuggest();
        //яндекс саджест
      } else {
        setIsLoading(true);
        setIsDisabled(true);

        geocode(context, title)
          .then(results => handleGeocodeResults(results, title, title))
          .catch(() => {
            setError(ERROR);
            setIsLoading(false);
            setIsDisabled(false);
          });
      }

      updateLocalStorageFiltersWereApplied();
    },
    [
      context,
      currentLocation,
      handleGeocodeResults,
      onBuilderSelected,
      onGeoSelected,
      onHighwaySelected,
      onKPSelected,
      onBSCenterSelected,
      onCoworkingSelected,
      resetSuggest,
    ],
  );

  const handleLinkClick = React.useCallback((_: string, group: TSuggestType) => {
    if (group === ESuggestType.newbuilding) {
      trackNewbuildingSuggestLinkClick();
    }
  }, []);

  return (
    <div className={clsx(styles['container'], isRedesign && styles['redesign'])}>
      <div className={styles['addon-left']}>
        <IconActionSearch16 color={isRedesign ? 'icon-secondary-default' : 'icon-main-default'} />
      </div>

      <StructuredGeoSuggest
        suggestData={suggestData}
        value={value}
        selectedValue={selectedValue}
        placeholder={placeholder}
        disabled={isDisabled}
        error={error}
        inputStyle={styles['input']}
        popupStyle={isRedesign ? styles['popup'] : undefined}
        inputType={EInputType.search}
        onItemClick={handleItemClick}
        onValueChange={handleValueChange}
        onFocus={handleFocus}
        onLinkClick={handleLinkClick}
        onBlur={resetSuggest}
        itemsTruncation={ETruncation.Two}
      />

      <div className={styles['addon-right']}>
        {isLoading && (
          <div className={styles['spinner']}>
            <Spinner size={16} color="current_color" />
          </div>
        )}
        {rightAddon}
      </div>
    </div>
  );
};
